import * as JsonDecoder from "ts.data.json";
import {
  CategoryLeaf, CategoryBranch, ProductLeaf, ProductBranch,
  Category, Product, Section, SelectionGroup, Catalog,
  OutOfStockListItem,
  FulfillmentMethod,
  Availability,
  OutOfStockList,
} from "./types/catalog";

import {
  Cart, CartItem, CartItemsDiscounts, CartItemSelectionGroup
} from "./types/cart";
import { Temporal } from "@js-temporal/polyfill";
import { Deal, RestrictionType } from "src/storefront/types/storefront";
import { IdTarget } from "src/storefront/types/id_target";
import {
  OutOfStockResolutionPreference,
  OutOfStockResolutionPreferenceType
} from "src/storefront/types/out_of_stock_resolution_preference";
import { undefinedOrNullDecoder } from "src/common_decoders";

const CategoryLeafDecoder = JsonDecoder.object<ICategoryLeaf>({
  key: JsonDecoder.string(),
  title: JsonDecoder.string(),
  products: JsonDecoder.lazy(() => JsonDecoder.array(ProductDecoder, "Product[]")),
  description: undefinedOrNullDecoder(JsonDecoder.string()),
}, "CategoryLeaf");

const CategoryBranchDecoder : JsonDecoder.Decoder<ICategoryBranch> = JsonDecoder.object<ICategoryBranch>({
  key: JsonDecoder.string(),
  title: JsonDecoder.string(),
  categories: JsonDecoder.lazy(() => JsonDecoder.array<ICategory>(CategoryDecoder, "Category[]")),
  description: undefinedOrNullDecoder(JsonDecoder.string()),
}, "CategoryBranch");

const ProductUnitPriceDecoder: JsonDecoder.Decoder<IProductUnitPrice> = JsonDecoder.object({
  price: JsonDecoder.number(),
  unit_quantity: JsonDecoder.number(), 
  unit_measure: JsonDecoder.string() 
}, "ProductUnitPriceDecoder");

const ProductLeafDecoder : JsonDecoder.Decoder<IProductLeaf> = JsonDecoder.object<IProductLeaf>({
  key: JsonDecoder.string(),
  sku: undefinedOrNullDecoder(JsonDecoder.string()),
  title: JsonDecoder.string(),
  price: JsonDecoder.number(),
  tags: undefinedOrNullDecoder(JsonDecoder.array(JsonDecoder.string(), "tag[]")),
  description: undefinedOrNullDecoder(JsonDecoder.string()),
  images: undefinedOrNullDecoder(JsonDecoder.array(JsonDecoder.string(), "images[]")),
  max: undefinedOrNullDecoder(JsonDecoder.number()),
  default_quantity: undefinedOrNullDecoder(JsonDecoder.number()),
  restrictions: undefinedOrNullDecoder(JsonDecoder.array(JsonDecoder.enumeration(RestrictionType, "RestrictionType"), "restrictions[]")),
  popularity: undefinedOrNullDecoder(JsonDecoder.number()),
  size_label: undefinedOrNullDecoder(JsonDecoder.string()),
  unit_price: undefinedOrNullDecoder<IProductUnitPrice>(ProductUnitPriceDecoder),
}, "ProductLeaf");

const ProductBranchDecoder : JsonDecoder.Decoder<IProductBranch> = JsonDecoder.object({
  key: JsonDecoder.string(),
  sku: undefinedOrNullDecoder(JsonDecoder.string()),
  title: JsonDecoder.string(),
  price: JsonDecoder.number(),
  selection_groups: JsonDecoder.lazy(() => JsonDecoder.allOf([JsonDecoder.array(SelectionGroupDecoder, "SelectionGroup[]"), nonEmpty], "NonEmptySelectionGroup")),
  tags: undefinedOrNullDecoder(JsonDecoder.array(JsonDecoder.string(), "tag[]")),
  description: undefinedOrNullDecoder(JsonDecoder.string()),
  images: undefinedOrNullDecoder(JsonDecoder.array(JsonDecoder.string(), "images[]")),
  max: undefinedOrNullDecoder(JsonDecoder.number()),
  restrictions: undefinedOrNullDecoder(JsonDecoder.array(JsonDecoder.string(), "restrictions[]")),
  popularity: undefinedOrNullDecoder(JsonDecoder.number()),
  size_label: undefinedOrNullDecoder(JsonDecoder.string()),
  unit_price: undefinedOrNullDecoder<IProductUnitPrice>(ProductUnitPriceDecoder),
}, "ProductBranch");

const CategoryDecoder = JsonDecoder.oneOf<ICategory>([CategoryBranchDecoder, CategoryLeafDecoder], "CategoryDecoder");
const ProductDecoder = JsonDecoder.oneOf<IProduct>([ProductBranchDecoder, ProductLeafDecoder], "ProductDecoder");

const PlainTimeDecoder: JsonDecoder.Decoder<Temporal.PlainTime> = JsonDecoder.string().map((str) => {
  return Temporal.PlainTime.from(str, { overflow: 'reject' });
});

const SelectionGroupDecoder: JsonDecoder.Decoder<ISelectionGroup> = JsonDecoder.object({
  key: JsonDecoder.string(),
  title: JsonDecoder.string(),
  description: undefinedOrNullDecoder(JsonDecoder.string()),
  min: JsonDecoder.fallback(0, JsonDecoder.number()),
  max: undefinedOrNullDecoder(JsonDecoder.number()),
  products: JsonDecoder.array(ProductDecoder, "Product[]"),
}, "SelectionGroup");

const AvailabilityDecoder: JsonDecoder.Decoder<IAvailability> = JsonDecoder.object({
  start_time: PlainTimeDecoder,
  end_time: PlainTimeDecoder,
  wday: JsonDecoder.number(),
}, "Availability");

const DeliveryFulfillmentDecoder = JsonDecoder.literal("delivery");
const PickupFulfillmentDecoder = JsonDecoder.literal("pickup");
const FulfillmentMethodDecoder = JsonDecoder.oneOf([DeliveryFulfillmentDecoder, PickupFulfillmentDecoder], "FulfillmentMethod");

const SectionDecoder = JsonDecoder.object<ISection>({
  key: JsonDecoder.string(),
  title: JsonDecoder.string(),
  categories: JsonDecoder.array(CategoryDecoder, "Category[]"),
  fulfillment_methods: JsonDecoder.array(FulfillmentMethodDecoder, "FulfillmentMethod[]"),
  description: undefinedOrNullDecoder(JsonDecoder.string()),
  availabilities: JsonDecoder.array(AvailabilityDecoder, "Availability[]"),
}, "Section"); // .map(d => new Section(d.key, d.title, d.description, d.categories));

const CatalogDecoder: JsonDecoder.Decoder<ICatalog> = JsonDecoder.object({
  sections: JsonDecoder.array(SectionDecoder, "Section[]"),
}, "Catalog"); // .map(d => new Catalog(d.sections));

 // eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseCatalog = (c: any) : Catalog => {
  const result = CatalogDecoder
    .decode(c)
    .map(tree => assembleCatalog(tree));

  if (result.isOk()) {
    return result.value;
  } else {
    throw new Error(result.error);
  }
};

interface IAvailability {
  start_time: Temporal.PlainTime;
  end_time: Temporal.PlainTime;
  wday: number;
}

interface ICatalog {
  sections: ISection[];
}

interface ISection {
  key: string;
  title: string;
  categories: ICategory[];
  fulfillment_methods: string[];
  description?: string;
  availabilities: IAvailability[];
}

interface ISelectionGroup {
  key: string;
  title: string;
  products: IProduct[];
  min: number;
  max?: number;
  description?: string;
}

interface ICategoryLeaf {
  key: string;
  title: string;
  products: IProduct[];
  description?: string;
}

interface ICategoryBranch {
  key: string;
  title: string;
  categories: ICategory[];
  description?: string;
}

interface IProductLeaf {
  key: string;
  sku?: string;
  title: string;
  price: number;
  tags?: string[];
  description?: string;
  images?: string[];
  max?: number;
  default_quantity?: number;
  restrictions?: string[];
  popularity?: number;
  size_label?: string;
  unit_price?: IProductUnitPrice;
}

interface IProductBranch {
  key: string;
  sku? :string;
  title: string;
  price: number;
  selection_groups: ISelectionGroup[];
  tags?: string[];
  description?: string;
  images?: string[];
  max?: number;
  restrictions?: string[];
  popularity?: number;
  size_label?: string;
  unit_price?: IProductUnitPrice;
}

type ICategory = ICategoryLeaf | ICategoryBranch;
type IProduct = IProductLeaf | IProductBranch;

const assembleCatalog = ({ sections }: ICatalog ) : Catalog => {
  return new Catalog(sections.map(s => assembleSection(s)));
};

const assembleSection = ({ key, title, description, fulfillment_methods, categories, availabilities }: ISection) : Section => {
  const coerceFulfillmentMethod = (c: string): FulfillmentMethod => {
    return c as FulfillmentMethod;
  }

  return new Section({
    key: key,
    keyPrefix: [],
    title: title,
    categories: categories.map(c => assembleCategory(c, [key])),
    fulfillmentMethods: fulfillment_methods.map(c => coerceFulfillmentMethod(c)),
    description: description,
    availabilities: availabilities.map(a => assembleAvailability(a)),
  });
}

const assembleAvailability = ({ start_time, end_time, wday }: IAvailability) : Availability => {
  return new Availability(
    start_time,
    end_time,
    wday,
  );
}

const assembleCategoryLeaf = ({ key, title, products, description }: ICategoryLeaf, keyPrefix: string[]): CategoryLeaf => {
  return new CategoryLeaf(key, keyPrefix, title, products.map(p => assembleProduct(p, keyPrefix.concat(key))), description);
};

const assembleCategoryBranch = ({ key, title, categories, description }: ICategoryBranch, keyPrefix: string[]): CategoryBranch => {
  return new CategoryBranch(key, keyPrefix, title, categories.map(c => assembleCategory(c, keyPrefix.concat(key))), description);
}

const assembleCategory = (c: ICategory, keyPrefix: string[]) : Category => {
  if ("products" in c)
    return assembleCategoryLeaf(c, keyPrefix);
  else
    return assembleCategoryBranch(c, keyPrefix);
}
export interface IProductUnitPrice {
  price: number,
  unit_quantity: number,
  unit_measure: string,
}

export const assembleProductUnitPriceLabel = ({ price, unit_quantity, unit_measure } : IProductUnitPrice) : string => {
  const priceLabel = price < 100 ? `${price}c` : `$${(price / 100).toFixed(2)}`

  if (unit_measure.length <= 2) {
    // Assume <= 2 means that it's a SI unit of measure to attempt to avoid special casing named descriptors.
    return `${priceLabel} / ${unit_quantity}${unit_measure}`;
  } else if (unit_quantity > 1) {
    return `${priceLabel} / ${unit_quantity} ${unit_measure}`
  } else if (unit_measure === "each") {
    return `${priceLabel} ${unit_measure}`
  } else {
    return `${priceLabel} / ${unit_measure}`
  }
}

const assembleProductLeaf = ({ key, title, price, tags, sku, description, images, max, default_quantity, restrictions, popularity, size_label, unit_price } : IProductLeaf, keyPrefix: string[]): ProductLeaf => {
  return new ProductLeaf({
    key: key,
    keyPrefix: keyPrefix,
    title: title,
    price: price,
    tags: tags || [],
    sku: sku,
    description: description,
    images: images || [],
    maxQuantity: max,
    defaultQuantity: default_quantity,
    restrictions: restrictions,
    popularity: popularity,
    sizeLabel: size_label,
    unitPriceLabel: unit_price && assembleProductUnitPriceLabel(unit_price),
  });
}

const assembleProductBranch = ({ key, title, price, selection_groups, tags, sku, description, images, max, restrictions, popularity, size_label, unit_price }: IProductBranch, keyPrefix: string[]): ProductBranch => {
  const selectionGroups = selection_groups.map(sg => assembleSelectionGroup(sg, keyPrefix.concat(key)));
  const score = (sg: SelectionGroup) : number => {
    if (sg.minSelections === 1 && sg.maxSelections === 1)
      return 2;
    else if (sg.minSelections === 1)
      return 1;
    else
      return 0;
  };

  selectionGroups.sort((a, b) => {
    return score(b) - score(a);
  });

  return new ProductBranch({
    key: key,
    keyPrefix: keyPrefix,
    title: title,
    price: price,
    selectionGroups: selectionGroups,
    tags: tags || [],
    sku: sku,
    description: description,
    images: images || [],
    maxQuantity: max,
    restrictions: restrictions,
    popularity: popularity,
    sizeLabel: size_label,
    unitPriceLabel: unit_price && assembleProductUnitPriceLabel(unit_price),
  });
}

const assembleProduct = (p: IProduct, keyPrefix: string[]) : Product => {
  if ("selection_groups" in p)
    return assembleProductBranch(p, keyPrefix);
  else
    return assembleProductLeaf(p, keyPrefix);
};

const assembleSelectionGroup = ({ key, title, products, min, max, description }: ISelectionGroup, keyPrefix: string[]) : SelectionGroup => {
  return new SelectionGroup(
    key,
    keyPrefix,
    title,
    products.map(p => assembleProduct(p, keyPrefix.concat(key))),
    min,
    max,
    description,
  );
};

const nonEmpty = new JsonDecoder.Decoder<ISelectionGroup[]>((json: ISelectionGroup[]) => {
  if (json.length > 0) {
    return JsonDecoder.ok(json);
  } else {
    return JsonDecoder.err('Set is empty');
  }
});

const CartItemSelectionGroupDecoder: JsonDecoder.Decoder<ICartItemSelectionGroup> = JsonDecoder.object({
  key: JsonDecoder.string(),
  key_prefix: JsonDecoder.array(JsonDecoder.string(), "KeyPrefix"),
  name: JsonDecoder.string(),
  cart_items: JsonDecoder.lazy(() => JsonDecoder.array(CartItemDecoder, "CartItem[]")),
}, "CartItemSelectionGroup");

const CartItemDiscountDecoder = JsonDecoder.object({
  amount: JsonDecoder.number(),
  name: JsonDecoder.string()
}, "CartItemDiscountEntry");
const CartItemDiscountsDecoder = JsonDecoder.record(CartItemDiscountDecoder, "CartItemDiscounts");
const CartItemsDiscountsDecoder = JsonDecoder.record(CartItemDiscountsDecoder, "CartItemsDiscounts");

const CartItemDecoder: JsonDecoder.Decoder<ICartItem> = JsonDecoder.object({
  id: undefinedOrNullDecoder(JsonDecoder.string()),
  key: JsonDecoder.string(),
  key_prefix: JsonDecoder.array(JsonDecoder.string(), "KeyPrefix"),
  name: JsonDecoder.string(),
  description: undefinedOrNullDecoder(JsonDecoder.string()),
  price: JsonDecoder.number(),
  tags: JsonDecoder.array(JsonDecoder.string(), "Tags"),
  quantity: JsonDecoder.number(),
  meal_preferences: undefinedOrNullDecoder(JsonDecoder.string()),
  selection_groups: JsonDecoder.array(CartItemSelectionGroupDecoder, "SelectionGroup[]"),
  restrictions: JsonDecoder.array(JsonDecoder.string(), "Restriction[]"),
  out_of_stock_resolution_preference: undefinedOrNullDecoder(JsonDecoder.oneOf([JsonDecoder.literal("substitute"), JsonDecoder.isExactly("refund_item")], "OutOfStockResolutionPreferenceType")),
}, "CartItem"); // .

const CartDecoder = JsonDecoder.object<IResponse>({
  bag: JsonDecoder.array(CartItemDecoder, "CartItem[]"),
  uuid: JsonDecoder.string(),
  discount: JsonDecoder.number(),
  delivery_fee: JsonDecoder.number(),
  subscription_delivery_fee_savings: JsonDecoder.number(),
  subscription_savings_on_fees: JsonDecoder.optional(JsonDecoder.number()),
  service_fee: JsonDecoder.number(),
  eligible_free_product_keys: JsonDecoder.array(JsonDecoder.string(), "FreeProducts[]"),
  items_discounts: CartItemsDiscountsDecoder,
  alert_message: undefinedOrNullDecoder(JsonDecoder.string()),
}, "Cart");

export type CartWithAlertMessage = Cart & { alertMessage?: string }

 // eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseCart = (c: any) : CartWithAlertMessage => {
  const result = CartDecoder
    .decode(c)
    .map((tree) => {
      return Object.assign(
        assembleCart({
          cart_items: tree.bag,
          id: tree.uuid.toString(),
          deliveryFee: tree.delivery_fee,
          subscriptionSavingsOnFees: tree.subscription_savings_on_fees ?? tree.subscription_delivery_fee_savings,
          serviceFee: tree.service_fee,
          discount: tree.discount,
          itemsDiscounts: tree.items_discounts,
          eligible_free_product_keys: tree.eligible_free_product_keys,
        }),
        { alertMessage: tree.alert_message }
      )
    });

  if (result.isOk()) {
    return result.value;
  } else {
    throw new Error(result.error);
  }
};

interface IResponse {
  bag: ICartItem[];
  uuid: string;
  delivery_fee: number;
  subscription_delivery_fee_savings: number;
  subscription_savings_on_fees?: number;
  service_fee: number;
  discount: number;
  eligible_free_product_keys: string[];
  items_discounts: CartItemsDiscounts;
  alert_message?: string;
}

export interface ICart {
  id: string;
  deliveryFee: number;
  subscriptionSavingsOnFees: number;
  serviceFee: number;
  discount: number;
  cart_items: ICartItem[];
  itemsDiscounts: CartItemsDiscounts;
  eligible_free_product_keys: string[];
}

export interface ICartItem {
  id?: string;
  key: string;
  key_prefix: string[];
  name: string;
  description?: string;
  price: number;
  tags: string[];
  quantity: number;
  meal_preferences?: string;
  selection_groups: ICartItemSelectionGroup[];
  restrictions: string[];
  out_of_stock_resolution_preference: OutOfStockResolutionPreferenceType | undefined;
}

export interface ICartItemSelectionGroup {
  key: string;
  key_prefix: string[];
  name: string;
  cart_items: ICartItem[];
}

export interface IProductDealDetails {
  product_keys: Array<string>;
  product_deal_text: string;
}

const ProductDealDecoder = JsonDecoder.object<IProductDealDetails>({
  product_keys: JsonDecoder.array(JsonDecoder.string(), "ProductKeys[]"),
  product_deal_text: JsonDecoder.string(),
}, "ProductDeal");

export interface IDeal {
  id: number;
  title: string;
  description: string;
  terms_and_conditions?: string;
  product_details?: IProductDealDetails;
}

const DealDecoder = JsonDecoder.object<IDeal>({
  id: JsonDecoder.number(),
  title: JsonDecoder.string(),
  description: JsonDecoder.string(),
  terms_and_conditions: undefinedOrNullDecoder(JsonDecoder.string()),
  product_details: undefinedOrNullDecoder(ProductDealDecoder),
}, "Deal");

const DealListDecoder = JsonDecoder.array<IDeal>(DealDecoder, "DealList");

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseDealList = (d: any[]) : Deal[] => {
  const result = DealListDecoder
    .decode(d)
    .map(deals => deals.map(
      deal => assembleDeal(deal)
    ));

  if (result.isOk()) {
    return result.value;
  } else {
    throw new Error(result.error);
  }
}

const assembleDeal = (deal: IDeal) : Deal => {
  const assembledDeal: Deal = { id: deal.id, title: deal.title, description: deal.description, termsAndConditions: deal.terms_and_conditions }

  if (deal.product_details) {
    assembledDeal.productDetails = { productKeys: deal.product_details.product_keys, productDealText: deal.product_details.product_deal_text }
  }

  return assembledDeal;
}

const assembleCart = (cart: ICart): Cart => {
  return new Cart(
    cart.id,
    cart.cart_items.map(c => assembleCartItem(c, cart.itemsDiscounts)),
    cart.deliveryFee,
    cart.subscriptionSavingsOnFees,
    cart.serviceFee,
    cart.discount,
    cart.eligible_free_product_keys,
  );
}

const assembleCartItem = (cart_item: ICartItem, cartItemsDiscounts: CartItemsDiscounts) : CartItem => {
  return new CartItem(
    cart_item.key,
    cart_item.key_prefix,
    cart_item.name,
    cart_item.price,
    cart_item.quantity,
    cart_item.selection_groups.map(sg => assembleCartItemSelectionGroup(sg)),
    new IdTarget(cart_item.id),
    cart_item.out_of_stock_resolution_preference ? OutOfStockResolutionPreference.fromType(cart_item.out_of_stock_resolution_preference) : OutOfStockResolutionPreference.Default,
    cart_item.description,
    cart_item.meal_preferences,
    cart_item.restrictions,
    cart_item.id ? cartItemsDiscounts[cart_item.id] : {},
  );
};

const assembleCartItemSelectionGroup = (cartItemSelectionGroup: ICartItemSelectionGroup) : CartItemSelectionGroup => {
  return new CartItemSelectionGroup(
    cartItemSelectionGroup.key,
    cartItemSelectionGroup.key_prefix,
    cartItemSelectionGroup.name,
    cartItemSelectionGroup.cart_items.map(c => assembleCartItem(c, {})),
  );
}

export interface IOutOfStockListItem {
  snooze_start: string;
  snooze_end: string;
  key?: string;
  sku?: string;
}

const OutageListItemDecoder : JsonDecoder.Decoder<IOutOfStockListItem> = JsonDecoder.object({
  snooze_start: JsonDecoder.string(),
  snooze_end: JsonDecoder.string(),
  key: undefinedOrNullDecoder(JsonDecoder.string()),
  sku: undefinedOrNullDecoder(JsonDecoder.string()),
}, "IOutageListItem");

const OutOfStockListDecoder: JsonDecoder.Decoder<IOutOfStockListItem[]> = JsonDecoder.array(OutageListItemDecoder, "IOutageListItem[]");

const assembleOutOfStockList = (loadedAt: Temporal.Instant, items: IOutOfStockListItem[]): OutOfStockList => {
  return new OutOfStockList({
    loadedAt: loadedAt,
    outOfStockListItems: items.map(assembleOutOfStockListItem),
  });
}

const assembleOutOfStockListItem = (item: IOutOfStockListItem) : OutOfStockListItem => {
  return new OutOfStockListItem({
    snoozeStart: Temporal.Instant.from(item.snooze_start),
    snoozeEnd: Temporal.Instant.from(item.snooze_end),
    key: item.key,
    sku: item.sku,
  });
}

 // eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseOutOfStockList = (loadedAt: Temporal.Instant, c: any) : OutOfStockList => {
  const result = OutOfStockListDecoder
    .decode(c)
    .map((d) => assembleOutOfStockList(loadedAt, d))

  if (result.isOk()) {
    return result.value;
  } else {
    throw new Error(result.error);
  }
}
